import { api, API_BASE } from "@/lib/apiClient";

export type AufmassFieldKind = "text" | "mm" | "long" | "yesno" | "yesno_unklar" | "choice" | "multi";

export interface AufmassFieldOption {
  value: string;
  label: string;
}

export interface AufmassField {
  id: string;
  label: string;
  kind: AufmassFieldKind;
  hint?: string | null;
  options: AufmassFieldOption[];
}

export interface AufmassSection {
  id: string;
  title: string;
  fields: AufmassField[];
}

export type AufmassValue = string | string[];

export interface AufmassSheet {
  id: number;
  auftrag_id: number;
  felder: Record<string, AufmassValue>;
  titel: string;
  updated_at?: string | null;
}

export interface AufmassState {
  schema: AufmassSection[];
  auftraggeber?: string | null;
  auftragsnummer?: string | null;
  sheets: AufmassSheet[];
  created_id?: number;
}

function textField(value: unknown): string {
  return value == null ? "" : String(value);
}

export function normalizeAufmassFelder(
  schema: AufmassSection[],
  raw: Record<string, AufmassValue> | null | undefined,
): Record<string, AufmassValue> {
  const source = raw || {};
  const out: Record<string, AufmassValue> = {};
  for (const section of schema) {
    for (const field of section.fields) {
      const value = source[field.id];
      if (field.kind === "multi") {
        const items = Array.isArray(value) ? value : [];
        const allowed = new Set(field.options.map((option) => option.value));
        out[field.id] = items.map(String).filter((item) => allowed.has(item));
      } else {
        out[field.id] = textField(value);
      }
    }
  }
  return out;
}

export function getAuftragAufmass(orderId: number): Promise<AufmassState> {
  return api.get<AufmassState>(`/auftraege/${encodeURIComponent(String(orderId))}/aufmass`, {
    cache: "no-store",
  });
}

export function createAuftragAufmass(orderId: number): Promise<AufmassState> {
  return api.post<AufmassState>(`/auftraege/${encodeURIComponent(String(orderId))}/aufmass`, {});
}

export function updateAuftragAufmass(
  orderId: number,
  sheetId: number,
  felder: Record<string, AufmassValue>,
): Promise<AufmassState> {
  return api.put<AufmassState>(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmass/${encodeURIComponent(String(sheetId))}`,
    { felder },
  );
}

export function deleteAuftragAufmass(orderId: number, sheetId: number): Promise<AufmassState> {
  return api.del<AufmassState>(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmass/${encodeURIComponent(String(sheetId))}`,
  );
}

async function fetchPdf(url: string): Promise<Blob> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed?.detail) message = parsed.detail;
    } catch {
      /* plain text */
    }
    throw new Error(message);
  }
  return res.blob();
}

export function fetchAufmassSheetPdf(orderId: number, sheetId: number): Promise<Blob> {
  return fetchPdf(
    `${API_BASE || ""}/auftraege/${encodeURIComponent(String(orderId))}/aufmass/${encodeURIComponent(String(sheetId))}/pdf`,
  );
}
